import { useEffect, useMemo } from 'react';
import {
  classifyEmailChoiceParam,
  resolveEmailPollOptionId,
  resolveEmailPollOptionIdFromDisplaySlot,
  resolveEmailAnswerCanonicalIndex,
  type EmailAnswerQuestionFields,
  type EmailPollChoiceQuestionFields,
} from '@/lib/quiz/emailAnswerIntent';

export type QuizEmailAnswerIntent =
  | { status: 'none' }
  | { status: 'invalid' }
  | { status: 'pending' }
  | { status: 'ready'; canonicalIndex: number; pollOptionId?: undefined }
  | { status: 'ready'; pollOptionId: string; canonicalIndex?: undefined };

type Options = {
  emailChoiceParam: string | null;
  pollChoiceParam?: string | null;
  firstQuestion?: (EmailAnswerQuestionFields & EmailPollChoiceQuestionFields) | null;
  authResolved: boolean;
  questionsReady: boolean;
};

/**
 * Newsletter deep-link intent: wait for identity + user-scoped questions, then expose
 * the canonical answer index for the game machine to apply on question 0.
 */
export function useQuizEmailAnswerIntent({
  emailChoiceParam,
  pollChoiceParam,
  firstQuestion,
  authResolved,
  questionsReady,
}: Options): QuizEmailAnswerIntent {
  const intent = useMemo(() => {
    const hasPollChoice = pollChoiceParam != null;
    const classified = classifyEmailChoiceParam(emailChoiceParam);
    if (classified.kind === 'none' && !hasPollChoice) return { status: 'none' } as const;
    if (classified.kind === 'invalid') return { status: 'invalid' } as const;

    if (!authResolved || !questionsReady || !firstQuestion) {
      return { status: 'pending' } as const;
    }

    const isPollQuestion = firstQuestion.item_type === 'poll';

    if (hasPollChoice) {
      const pollOptionId = resolveEmailPollOptionId(pollChoiceParam, firstQuestion);
      return pollOptionId
        ? ({ status: 'ready', pollOptionId } as const)
        : ({ status: 'invalid' } as const);
    }

    if (classified.kind !== 'valid') {
      return { status: 'invalid' } as const;
    }

    if (isPollQuestion) {
      const pollOptionId = resolveEmailPollOptionIdFromDisplaySlot(
        classified.displaySlot,
        firstQuestion
      );
      return pollOptionId
        ? ({ status: 'ready', pollOptionId } as const)
        : ({ status: 'invalid' } as const);
    }

    return {
      status: 'ready' as const,
      canonicalIndex: resolveEmailAnswerCanonicalIndex(classified.displaySlot, firstQuestion),
    };
  }, [emailChoiceParam, pollChoiceParam, firstQuestion, authResolved, questionsReady]);

  useEffect(() => {
    if (intent.status !== 'ready' && intent.status !== 'invalid') return;
    if (emailChoiceParam == null && pollChoiceParam == null) return;
    if (import.meta.env.SSR) return;
    const url = new URL(globalThis.location.href);
    let changed = false;
    for (const key of ['email_choice', 'poll_choice', 'answer_index'] as const) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (!changed) return;
    globalThis.history.replaceState(
      globalThis.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`
    );
  }, [intent.status, emailChoiceParam, pollChoiceParam]);

  return intent;
}
